// Shared inputs for the itinerary engine. Pure data (plain Maps), so the
// engine has no Supabase dependency and is fully unit-testable from fixtures.

import { liftSuitabilityAllows, type LiftSuitability } from "./lift-suitability";
import { planaiTrailWindow } from "./operations/planai";
import type { PlannerPreferences } from "./preferences";
import type { PlannerGraph, PlannerNode } from "./graph-types";

export type LiftStatus = "open" | "closed" | "hold" | "scheduled" | "unknown";
export type TrailStatus = "open" | "closed" | "hold" | "unknown";

export type EngineTrailInfo = {
  scoreNotes?: string[];
  name: string | null;
  ref: string | null;
  osmId?: string | null;
  aspectCardinal: string | null;
  meanElevationM: number | null;
  treeCoverFraction: number | null;
  groomedToday: boolean | null;
  statusSource: string | null;
  statusUpdatedAt: string | null;
};

export type EngineLiftInfo = {
  /** Retained source identity for explicit local-pass membership. */
  osmId?: string | null;
  /** Explicit published closing evidence; absence keeps return times unknown. */
  closingEvidence?: import("./lift-closing").LiftClosingEvidence | null;
  downloadClosingEvidence?: import("./lift-closing").LiftClosingEvidence | null;
  suitability?: LiftSuitability;
  suitabilityChecked?: boolean;
  operatingWindows?: { open: number; close: number }[];
  /** Independently published descent windows; never widen uphill boarding. */
  downloadOperatingWindows?: { open: number; close: number }[];
  scheduleSource?: string | null;
  scheduleNote?: string | null;
  statusObservedAt?: string | null;
  name: string | null;
  liftType: string;
  capacityPerHour: number | null;
  topElevationM: number | null;
  /** Local decimal hour the lift starts running. */
  openHour: number;
  /** Local decimal hour the lift stops running. */
  closeHour: number;
};

export type EngineInputs = {
  /** Request-local permitted lifts after a local pass is selected. */
  passLiftIds?: ReadonlySet<string>;
  passBlockedDownhillLiftIds?: ReadonlySet<string>;
  /** Local route date for dated operator access evidence. */
  planningDate?: string;
  graph: PlannerGraph;
  /** trailId -> hour -> score 0..100 */
  trailScore: Map<string, Map<number, number>>;
  /** trailId -> hour -> lapse-adjusted temperature C */
  trailTemp: Map<string, Map<number, number>>;
  trailInfo: Map<string, EngineTrailInfo>;
  trailStatus?: Map<string, TrailStatus>;
  liftStatus: Map<string, LiftStatus>;
  liftInfo: Map<string, EngineLiftInfo>;
  nodeById: Map<string, PlannerNode>;
  /** Local decimal hour the sun drops; trails cannot be skied after this. */
  sunsetHour: number | null;
  /** Day-mean v7 visibility factor below 0.6: trees keep contrast. */
  flatLight: boolean;
  crowdFactor: number;
  /** Area-wide status is limited. Unknown lifts still require explicit preview opt-in. */
  scheduledFallback: boolean;
  /**
   * Per-objective signals, all OPTIONAL so existing fixtures (which omit them)
   * keep the engine's identity behaviour. Assembled once per request in
   * engine-data.ts when any objective weight is non-zero.
   */
  /** trailId -> hour -> aspect-modulated solar ratio (0 in shadow, ~1 neutral). */
  solarRatioByTrailHour?: Map<string, Map<number, number>>;
  /** trailId -> scenic appeal in [0, 1]. */
  scenicScoreByTrail?: Map<string, number>;
  /** liftId -> hour -> queue index in [0, 1]. */
  congestionByLiftHour?: Map<string, Map<number, number>>;
  /** liftId -> hour -> estimated added wait minutes (the quiet cost). */
  waitMinutesByLiftHour?: Map<string, Map<number, number>>;
  /** trailId -> local appeal multiplier. Omitted maps keep legacy neutral 1.0. */
  runAppealByTrail?: Map<string, number>;
  /** trailId -> source of the appeal multiplier. */
  appealSourceByTrail?: Map<string, "curated" | "heuristic">;
  /** trailId -> verified curated note, absent for heuristic appeal. */
  appealNoteByTrail?: Map<string, string>;
};

export type EngineOptions = {
  sport?: PlannerPreferences["sport"];
  /** Explicit self-reported ability, never derived from day style or grade ceiling. */
  ability?: PlannerPreferences["ability"];
  includeUnknownLifts: boolean;
  allowBus: boolean;
  /** Concrete known lift types the skier has banned. Unknown types are absent. */
  bannedLiftTypes?: ReadonlySet<string>;
  /** MTB "no lift pass" mode: refuse every lift edge so the day is pedalled.
   * Optional so existing EngineOptions builders (and ski) keep prior behaviour:
   * omitted is treated as false (lifts allowed). */
  pedalOnly?: boolean;
};

/** Validate actual Gregorian dates without normalising impossible dates or
 * allocating Date objects inside the frequently called traversal gate. */
function calendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4)), month = Number(value.slice(5, 7)), day = Number(value.slice(8, 10));
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return day <= (month === 2 ? leap ? 29 : 28 : [4, 6, 9, 11].includes(month) ? 30 : 31);
}

/** Whether an edge may be traversed at all (lift status, bus opt-in, lift pass). */
export function isEdgeTraversable(
  edge: PlannerGraph["edges"][number],
  inputs: EngineInputs,
  opts: EngineOptions,
): boolean {
  if (edge.kind === "lift") {
    if (inputs.passLiftIds && !inputs.passLiftIds.has(edge.id)) return false;
    if (edge.direction === "down" && inputs.passBlockedDownhillLiftIds?.has(edge.id)) return false;
    // No lift pass: every uplift is off limits, in both the forward search and
    // the reachability home pass (both route through this gate), so a plan is
    // never built on, nor certified home by, a lift the rider will not take.
    if (opts.pedalOnly) return false;
    const info = inputs.liftInfo.get(edge.id);
    if (edge.suitabilityChecked === false || info?.suitabilityChecked === false) return false;
    // A persisted specialist marker needs current independently resolved lift
    // evidence. Unknown versions and missing evidence cannot certify access.
    if (edge.suitability !== undefined && (opts.sport !== "ski" || !info?.suitability
      || !liftSuitabilityAllows(edge.suitability, opts.ability)
      || edge.suitability.sourceLiftId !== info.suitability.sourceLiftId)) return false;
    if (info?.suitability !== undefined && (opts.sport !== "ski" || !liftSuitabilityAllows(info.suitability, opts.ability))) return false;
    const liftType = info?.liftType;
    if (liftType && opts.bannedLiftTypes?.has(liftType)) return false;
    const status = inputs.liftStatus.get(edge.id) ?? "unknown";
    if (status === "open") return true;
    if (status === "closed" || status === "hold") return false;
    if (status === "scheduled") return opts.includeUnknownLifts;
    // unknown
    return opts.includeUnknownLifts;
  }
  if (edge.kind === "bus") return opts.allowBus;
  if (edge.kind === "trail") {
    if (edge.conditionalAccess === "bike-park-pass-v2") {
      if (edge.accessChecked !== false || opts.sport !== "mtb" || opts.pedalOnly
        || !planaiTrailWindow(edge.bikeParkPass, inputs.planningDate, inputs.trailInfo.get(edge.id)?.osmId)) return false;
    } else if (edge.conditionalAccess !== undefined || edge.bikeParkPass !== undefined) {
      // accessChecked:false is intentional on this wire contract: deployed
      // older readers must refuse the edge until they understand its conditions.
      const pass: unknown = edge.bikeParkPass;
      if (edge.conditionalAccess !== "bike-park-pass-v1" || edge.accessChecked !== false || opts.pedalOnly
        || pass === null || typeof pass !== "object" || Array.isArray(pass)) return false;
      const { validFrom, validTo } = pass as Record<string, unknown>;
      if (!calendarDate(validFrom) || !calendarDate(validTo) || validFrom > validTo
        || !calendarDate(inputs.planningDate) || inputs.planningDate < validFrom || inputs.planningDate > validTo) return false;
    } else if (edge.accessChecked === false) return false;
    const status = inputs.trailStatus?.get(edge.id);
    if (status === "closed" || status === "hold") return false;
  }
  return true; // trail, link
}
